import { getWindowObject } from '@eventbrite/legacy-infra.feature-detection';
import { HEAP_EVENTS, HEAP_SCREENS, track } from '../../utils/analytics';
import { ExitModal } from '../exit-modal/ExitModal';
import styles from './ChatWindow.module.scss';

import React, { RefObject, useState } from 'react';

export interface ChatWindowProps {
    isLoggedIn: boolean;
    onClose: () => void;
    messagesSent: number;
    containerRef: RefObject<HTMLDivElement>;
    children: React.ReactNode;
}

/**
 * HOC that renders the window dressing.
 * The window dressing includes CTAs to Exit and additional modal.
 *
 * @param {ChatWindowProps} props
 * @returns {React.ReactNode}
 */
export const ChatWindow = (props: ChatWindowProps) => {
    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const handleClickOnBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setIsExitModalOpen(true);
        track({
            eventName: HEAP_EVENTS.BackButtonClick,
            eventData: {
                Screen: HEAP_SCREENS.CONCIERGE,
                messagesSent: props.messagesSent,
            },
        });
    };

    const handleSwitchToClassicSearch = () => {
        const storage = getWindowObject('localStorage');
        storage.setItem('nlsearch-experiment-opt-out', 'true');

        const location = getWindowObject('location');
        location.assign('/');
    };

    return (
        <div ref={props.containerRef} className={styles.root}>
            <div className={styles.headerContainer}>
                <div className={styles.primaryHeader}>
                    <a href="https://www.eventbrite.com/#" onClick={handleClickOnBack}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M14.5858 4.41797L7.0036 12.0002L14.5858 19.5823L16 18.1681L9.83203 12.0002L16 5.83218L14.5858 4.41797Z"
                                fill="#161719"
                            />
                        </svg>
                    </a>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M19.3791 15.6226H19.3819L17.1799 13.8682L12.2635 10.0842C12.1426 9.99184 12.2664 9.80516 12.3997 9.87945L15.2408 11.47C16.5285 12.1919 18.1562 11.7738 18.9362 10.5214C19.7581 9.19942 19.3238 7.45839 17.9733 6.67931L13.8036 4.26967C13.6722 4.19253 13.7683 3.99252 13.9112 4.04776L15.8265 4.81446C15.8265 4.81446 15.8761 4.83256 15.8932 4.83922C16.097 4.9097 16.3151 4.94875 16.5428 4.94875C17.5819 4.94875 18.4352 4.12681 18.5086 3.14962C18.6038 1.8848 17.6361 1.00095 16.4799 1.00095C15.9132 1.00095 7.62521 1 7.62521 1C6.48897 1 5.53369 1.92957 5.54893 3.06295C5.55559 3.66584 5.82704 4.20395 6.24896 4.57254C6.56707 4.8497 7.64045 5.70879 8.1519 6.11738C8.24334 6.19071 8.19095 6.33643 8.07476 6.33643H6.32325C4.44602 6.33643 2.93262 7.86507 2.93262 9.7404C2.93262 10.7595 3.37359 11.6624 4.0841 12.2853L14.0903 21.7914C14.856 22.5648 15.9294 23.0334 17.1123 22.9981C19.2981 22.9334 21.062 21.1209 21.0677 18.9361C21.0715 17.5741 20.4039 16.3664 19.3781 15.6235L19.3791 15.6226Z"
                            fill="#FF5010"
                        />
                    </svg>
                    {props.isLoggedIn ? (
                        <div>{/* Empty Div Used as Spacer */}</div>
                    ) : (
                        <a href="http://www.eventbrite.com/signin" className={styles.loginCTA}>
                            <div>Log In</div>
                        </a>
                    )}
                </div>
                <div className={styles.notificationBanner}>
                    <div className={styles.optOutBanner}>
                        We’re trying something new.{' '}
                        <a href="#" onClick={handleSwitchToClassicSearch}>
                            Switch to classic search
                        </a>
                    </div>
                </div>
            </div>
            {props.children}
            {isExitModalOpen && (
                <ExitModal
                    messagesSent={props.messagesSent}
                    onLeave={() => {
                        setIsExitModalOpen(false);
                        track({
                            eventName: HEAP_EVENTS.ExitModalLeave,
                            eventData: {
                                Screen: HEAP_SCREENS.CONCIERGE,
                                messagesSent: props.messagesSent,
                            },
                        });
                        props.onClose();
                    }}
                    onStay={() => {
                        setIsExitModalOpen(false);
                        track({
                            eventName: HEAP_EVENTS.ExitModalStay,
                            eventData: {
                                Screen: HEAP_SCREENS.CONCIERGE,
                                messagesSent: props.messagesSent,
                            },
                        });
                    }}
                />
            )}
        </div>
    );
};
